import { deleteRequest, getRequest, postRequest, putRequest, uploadRequest } from '@/lib/request'
import {
  NewContactFormOutputSchema,
  UpdateUserContactSchema,
} from '@/lib/schemas/authUser/userContact'
import { NoteFormSchema } from '@/lib/schemas/authUser/contacts'
import {
  CompanyContact,
  CompanyContactNote,
  CompanyContactWithCards,
  CompanyContactOrderOption,
} from '@/lib/types/CompanyContactT'
import { VCardOption } from '@/lib/types/VCardT'

export const getCompanyContacts = (params: {
  page: number
  size: number
  search?: string
  orderBy?: CompanyContactOrderOption['value']
  orderDirection?: 'asc' | 'desc'
  tagIds?: string[]
  vcardIds?: string[]
  joinedCompanyAtRange?: [string, string] | []
}): Promise<{ data: CompanyContactWithCards[]; total: number; page: number; lastPage: number }> => {
  return getRequest('/admin/companies/customers', params)
}

export const getCompanyContactDetail = (contactId: number): Promise<CompanyContact> => {
  return getRequest(`/admin/companies/customers/${contactId}`)
}

export const getContactVCards = (contactId: number): Promise<VCardOption[]> => {
  return getRequest(`/admin/companies/customers/${contactId}/vcards`)
}

export const getContactTags = (contactId: number): Promise<string[]> => {
  return getRequest(`/admin/companies/customers/${contactId}/tags`)
}

export const createCompanyContact = (
  params: NewContactFormOutputSchema
): Promise<CompanyContact> => {
  return postRequest('/admin/companies/customers', params, {
    showToast: true,
    pendingMessage: 'Creating contact...',
    successMessage: 'Contact created successfully!',
    errorMessage: 'Failed to create contact. Please try again.',
    customErrorMessagesByCode: {
      10003: 'User with this phone number already exists.',
    },
  })
}

export const downloadSampleContactsFile = () => {
  return getRequest('/admin/themes/import/users/sample', null, {
    responseType: 'blob',
    isDownload: true,
    fileName: 'sample_contacts.xlsx',
    showToast: true,
    pendingMessage: 'Preparing contact...',
    successMessage: 'Sample file downloaded successfully!',
    errorMessage: 'Failed to download sample file',
    headers: {
      'Content-Disposition': `attachment; filename="sample_contacts.xlsx"`,
    },
  })
}

export const uploadCompanyContacts = (params: { file: File }) => {
  return uploadRequest('/admin/companies/current/import', 'POST', params, {
    showToast: true,
    pendingMessage: 'Uploading contacts...',
    successMessage: 'Contacts uploaded successfully!',
    errorMessage: 'Failed to upload contacts. Please try again.',
  })
}

export const updateContact = (params: UpdateUserContactSchema & { id: number }) => {
  return putRequest(`/admin/companies/customers/${params.id}`, params, {
    showToast: true,
    pendingMessage: `Updating ${params.firstName} contact...`,
    successMessage: 'Contact updated successfully!',
    errorMessage: 'Failed to update contact. Please try again.',
  })
}

export const updateContactTags = (params: { tags: string[]; id: number }) => {
  return putRequest(`/admin/companies/customers/${params.id}/tags/sync`, params)
}

export const deleteContactTags = (params: { tags: string[]; id: number }) => {
  return putRequest(`/admin/companies/customers/${params.id}/tags/detach`, params)
}

export const getContactNotes = (params: { id: number }): Promise<CompanyContactNote[]> => {
  return getRequest(`/admin/companies/customers/${params.id}/notes`)
}

export const addContactNote = (
  params: NoteFormSchema & { id: number }
): Promise<CompanyContactNote> => {
  return postRequest(`/admin/companies/customers/${params.id}/notes`, params, {
    showToast: true,
    pendingMessage: `Adding note to contact...`,
    successMessage: 'Note added successfully!',
    errorMessage: 'Failed to add note. Please try again.',
  })
}

export const removeContactNote = (params: { id: number }) => {
  return deleteRequest(`/admin/companies/customers/notes/${params.id}`)
}

export const attachVCardsToContact = (params: { vcardsIds: number[]; id: number }) => {
  return putRequest(`admin/companies/customers/${params.id}/vcards/attach`, params, {
    showToast: true,
    pendingMessage: `Attaching vCards to contact...`,
    successMessage: 'vCards attached successfully!',
    errorMessage: 'Failed to attach vCards. Please try again.',
  })
}

export const detachVCardsFromContact = (params: {
  vcardsIds: number[]
  id: number
  showToast?: boolean
}) => {
  return putRequest(`/admin/companies/customers/${params.id}/vcards/detach`, params, {
    showToast: params.showToast ?? true,
    pendingMessage: `Detaching vCards from contact...`,
    successMessage: 'vCards detached successfully!',
    errorMessage: 'Failed to detach vCards. Please try again.',
  })
}

export const deleteContact = (contactId: number) => {
  return deleteRequest(`/admin/companies/customers/${contactId}`, null, {
    showToast: true,
    pendingMessage: 'Deleting contact...',
    successMessage: 'Contact deleted successfully!',
    errorMessage: 'Failed to delete contact. Please try again.',
  })
}

export const toggleStared = (params: {
  id: number
  toTrack: boolean
}): Promise<{ isStared: boolean }> => {
  return putRequest(`/admin/companies/customers/${params.id}/stared`, params)
}
